import { type ReactNode } from "react";
import { CircleAlert, Redo2 } from "lucide-react";
import { Textarea } from "../ui/textarea";
import { useFieldContext } from "./form-context";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Button } from "../ui/button";
import { cn, sanitizeHtml } from "@/lib/utils";
import { OverridesButton } from "../overrides-button";

export function FormTextArea({
  label,
  id,
  description,
  textareaClassName = "",
  readOnly = false,
  htmlContent,
  defaultSystemValue,
  hasOverriddenValue,
  openOverridesModal,
}: {
  label: ReactNode;
  id: string;
  description?: string;
  textareaClassName?: string;
  readOnly?: boolean;
  htmlContent?: string;
  defaultSystemValue?: string;
  hasOverriddenValue?: boolean;
  openOverridesModal?: () => void;
}) {
  const field = useFieldContext<string>();
  const sanitizedHtml = htmlContent ? sanitizeHtml(htmlContent) : null;
  const value = field?.state.value ?? "";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        {label && <div className="text-sm leading-none font-medium">{label}</div>}
        {defaultSystemValue !== undefined && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleAlert
                className={cn(
                  "w-4 h-4 text-orange-500",
                  value === defaultSystemValue && "invisible pointer-events-none"
                )}
              />
            </TooltipTrigger>
            <TooltipContent>
              <p>This value overwrites the default system value: {defaultSystemValue}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {defaultSystemValue !== undefined && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "text-orange-500 pb-0 pt-0 mr-auto",
                  value === defaultSystemValue && "invisible pointer-events-none"
                )}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  field?.setValue(defaultSystemValue ?? "");
                }}
              >
                <Redo2 className="w-2 h-2" />
                Reset to default
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Reset the value to the default system value: {defaultSystemValue}</p>
            </TooltipContent>
          </Tooltip>
        )}
        {hasOverriddenValue && <OverridesButton onClick={openOverridesModal!} />}
      </div>
      <Textarea
        id={id}
        value={field.state.value ?? ""}
        onChange={(e) => {
          field.handleChange(e.target.value);
        }}
        className={textareaClassName}
        readOnly={readOnly}
      />
      {description && <div className="text-xs form-description">{description}</div>}

      {htmlContent && (
        <div className="text-xs form-description" dangerouslySetInnerHTML={{ __html: sanitizedHtml || "" }} />
      )}
    </div>
  );
}
